import { useEffect, useCallback, useState, useRef, useMemo } from "react";

export const useFunction = (endpoint, options = {}) => {
  const [data, setData] = useState(new Map());
  const loading = useRef({});

  const defaultData = useMemo(() => ({ data: null, error: null, isLoading: true }), []);

  const reset = useCallback((id) => setData((prev) => new Map(prev).set(id, defaultData)), [defaultData]);

  const getData = useCallback(() => {
    data.forEach(async (entry, id) => {
      if (loading.current[id] || entry.error !== null || entry.data !== null) {
        return;
      }

      loading.current[id] = true;
      const refresh = () => reset(id);
      const result = { data: null, error: null, isLoading: false, refresh };
      try {
        result.data = await endpoint(id);
      } catch (e) {
        result.error = e;
      } finally {
        setData((prev) => new Map(prev).set(id, result));
        loading.current[id] = false;
      }
    });
  }, [data, endpoint, reset]);

  useEffect(() => {
    getData();
  }, [getData]);

  const memo = useCallback((id) => data.get(id) || defaultData, [data, defaultData]);

  return (id) => {
    if ((id || options.noArguments) && !data.has(id)) {
      reset(id);
    }
    return memo(id);
  };
};
